/**
 * Enrichment Service
 * 
 * The brain of Swirl's intelligence.
 * 
 * Every item must pass through this pipeline to ensure:
 * - Smart Insights work
 * - Memory Surfacing works
 * - Weekly Summaries are precise
 * - Predictions are possible
 * - Search is accurate
 * 
 * DO NOT skip enrichment unless explicitly testing.
 */

import { supabase } from '../lib/supabase';
import openaiService from './openaiService';
import fallbackClassifier from './fallbackClassifier';

/**
 * CONTROLLED VOCABULARY FOR THEMES
 * 
 * These are the ONLY allowed high-level themes.
 * Items get 1-3 themes max.
 */
const ALLOWED_THEMES = [
  'fashion',
  'style',
  'fitness',
  'exercise',
  'health',
  'nutrition',
  'sales',
  'business',
  'marketing',
  'productivity',
  'relationships',
  'travel',
  'restaurants',
  'food',
  'tech',
  'design',
  'creativity',
  'self improvement',
  'home',
  'money',
  'learning',
  'psychology',
  'lifestyle',
  'career',
];

/**
 * Extract clean text from item
 * Handles HTML, URLs, and various content types
 */
function extractCleanText(item) {
  const parts = [];
  
  // Title (always valuable)
  if (item.title) {
    parts.push(item.title);
  }
  
  // Description
  if (item.description) {
    parts.push(item.description);
  }
  
  // Text content (from OCR, transcription, or raw text)
  if (item.textContent) {
    // Remove HTML tags
    const cleaned = item.textContent
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    parts.push(cleaned);
  }
  
  // Existing tags as context
  if (item.tags && item.tags.length > 0) {
    parts.push(`Tags: ${item.tags.join(', ')}`);
  }
  
  // URL as signal (domain/path can be meaningful)
  if (item.url) {
    try {
      const url = new URL(item.url);
      const pathParts = url.pathname.split('/').filter(Boolean);
      if (pathParts.length > 0) {
        parts.push(`Context: ${pathParts.join(' ')}`);
      }
    } catch (e) {
      // Invalid URL, skip
    }
  }
  
  const fullText = parts.join('\n\n').substring(0, 8000); // Limit for API
  return fullText.trim();
}

/**
 * Generate embedding using OpenAI text-embedding-3-large
 */
async function generateEmbedding(text) {
  if (!text || text.length < 10) {
    if (import.meta.env.DEV) console.warn('[Enrichment] Text too short for embedding');
    return null;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      if (import.meta.env.DEV) console.warn('[Enrichment] No session for embedding');
      return null;
    }

    // Call Supabase edge function
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/deepsearch-auto-embed`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: text.substring(0, 8000) }),
      }
    );

    if (!response.ok) {
      if (import.meta.env.DEV) console.warn('[Enrichment] Embedding generation failed:', response.statusText);
      return null;
    }

    const result = await response.json();
    return result.embedding || null;
  } catch (error) {
    console.error('[Enrichment] Error generating embedding:', error);
    return null;
  }
}

/**
 * CORE ENRICHMENT LOGIC
 * 
 * Uses GPT-4o-mini to extract:
 * - Themes (from controlled vocabulary)
 * - Micro-topics (specific, human-readable)
 * - Entities (people, brands, places, etc.)
 * - AI analysis (summary, why it matters, related concepts)
 */
async function extractEnrichment(cleanText, itemContext = {}) {
  if (!cleanText || cleanText.length < 20) {
    // Minimal content - use fallback
    return {
      extractedThemes: ['lifestyle'],
      microTopics: ['quick save', 'bookmark'],
      entities: [],
      aiAnalysis: {
        summary: 'Minimal content saved for later review',
        whyItMatters: 'This was bookmarked quickly, likely for future reference',
        relatedConcepts: ['bookmarks', 'quick saves'],
      },
    };
  }

  try {
    const prompt = `You are analyzing content saved by a user in their personal knowledge system called Swirl.

Your job is to extract precise, human-level insights that power Smart Insights, Memory Surfacing, and AI predictions.

CONTENT:
${cleanText}

CONTEXT:
- URL: ${itemContext.url || 'N/A'}
- Source: ${itemContext.sourceType || 'N/A'}
- Channel: ${itemContext.channel || 'N/A'}

EXTRACT THE FOLLOWING:

1. THEMES (1-3 max)
Select ONLY from this list: ${ALLOWED_THEMES.join(', ')}
Choose the most direct matches. If content spans multiple worlds (e.g., "sales for fitness coaches"), include both.

2. MICROTOPICS (3-10 max)
These must be SPECIFIC and HUMAN-READABLE.

❌ BAD: "productivity tips", "sales ideas", "fashion content"
✅ GOOD: "cold email objection handling", "fall capsule wardrobe", "biceps hypertrophy routine"

Examples by domain:
- Fashion: "neutral layering", "tailored menswear", "streetwear silhouettes"
- Fitness: "lat activation drills", "push day variations", "lower back mobility"
- Sales: "objection handling scripts", "DM frameworks", "discovery call structure"
- Travel: "Japan garden itineraries", "minimalist travel gear", "Barcelona food map"
- Tech: "AI note-taking workflows", "Chrome extension UX patterns"

Make them specific to what's ACTUALLY in the content.

3. ENTITIES (0-10)
Extract: people, brands, locations, products, exercises, ingredients, creators, cities, landmarks, tools, apps, skills, objects.

Examples: "Nike", "Kyoto Bamboo Forest", "Notion", "Peter Thiel", "Bench press", "Canon R5"

4. AI ANALYSIS

a) summary (1-3 sentences):
- What is this actually about?
- What's the useful takeaway?
- What's the angle or insight?

b) whyItMatters (1-2 sentences):
- Why might this matter to the user?
- What does it reveal about their interests?
- What potential direction is emerging?

c) relatedConcepts (3-7 items):
Must be SPECIFIC, not generic.
Examples: "overshirt layering", "lat-focused pull sessions", "DM frameworks", "Kyoto day trips"

CRITICAL RULES:
- NO generic language
- NO vague summaries
- NO one-word themes
- ALWAYS produce real, human-level detail
- If content is truly minimal, infer likely intent and be upfront about it

Return ONLY valid JSON in this exact format:
{
  "extractedThemes": ["theme1", "theme2"],
  "microTopics": ["specific topic 1", "specific topic 2", ...],
  "entities": ["entity1", "entity2", ...],
  "aiAnalysis": {
    "summary": "...",
    "whyItMatters": "...",
    "relatedConcepts": ["concept1", "concept2", ...]
  }
}`;

    const messages = [
      {
        role: 'system',
        content: 'You are an expert content analyst. Extract precise, specific insights. Return only valid JSON.',
      },
      {
        role: 'user',
        content: prompt,
      },
    ];

    const response = await openaiService.chat(messages, {
      model: 'gpt-4o-mini',
      response_format: { type: 'json_object' },
      temperature: 0.3,
    });

    const result = JSON.parse(response);

    // Validate and clean themes
    if (result.extractedThemes) {
      result.extractedThemes = result.extractedThemes
        .map(t => t.toLowerCase().trim())
        .filter(t => ALLOWED_THEMES.includes(t))
        .slice(0, 3);
    }

    // Ensure microTopics are specific (not generic)
    if (result.microTopics) {
      result.microTopics = result.microTopics
        .filter(t => {
          const lower = t.toLowerCase();
          // Filter out generic garbage
          const isGeneric = 
            lower.includes('general') ||
            lower.includes('various') ||
            lower.includes('tips') ||
            lower.includes('ideas') ||
            lower.includes('content') ||
            lower === 'productivity' ||
            lower === 'sales' ||
            lower === 'fashion' ||
            lower === 'fitness';
          return !isGeneric && t.length > 3;
        })
        .slice(0, 10);
    }

    // Limit entities
    if (result.entities) {
      result.entities = result.entities.slice(0, 10);
    }

    // Validate AI analysis
    if (!result.aiAnalysis) {
      result.aiAnalysis = {
        summary: 'Content saved for reference',
        whyItMatters: 'This aligns with user interests',
        relatedConcepts: result.microTopics?.slice(0, 3) || [],
      };
    }

    return result;

  } catch (error) {
    console.error('[Enrichment] AI extraction failed:', error);
    if (import.meta.env.DEV) console.log('[Enrichment] Falling back to rule-based classification');
    
    // Use fallback classifier
    const fallback = fallbackClassifier.applyFallbackClassification({
      ...itemContext,
      textContent: cleanText,
    });
    
    return {
      extractedThemes: fallback.extractedThemes,
      microTopics: fallback.microTopics,
      entities: fallback.entities,
      aiAnalysis: fallback.aiAnalysis,
    };
  }
}

/**
 * MAIN ENRICHMENT FUNCTION
 * 
 * This is called by vaultWriterService.saveItem()
 * 
 * Input: Raw item (any structure)
 * Output: Fully enriched item
 */
export async function enrichItem(item, options = {}) {
  const { skipEmbedding = false } = options;
  
  if (import.meta.env.DEV) console.log('[Enrichment] Starting enrichment for:', item.title?.substring(0, 50) || item.id);
  
  try {
    // Step 1: Extract clean text
    const cleanText = extractCleanText(item);
    
    // Check if we need fallback BEFORE attempting AI enrichment
    const fallbackCheck = fallbackClassifier.needsFallback({
      ...item,
      textContent: cleanText,
    });
    
    if (!cleanText || cleanText.length < 10 || fallbackCheck.needsFallback) {
      if (import.meta.env.DEV) console.warn('[Enrichment] Insufficient data, using fallback classifier');
      if (import.meta.env.DEV) console.log('[Enrichment] Fallback reasons:', fallbackCheck.reasons);
      
      const fallback = fallbackClassifier.applyFallbackClassification({
        ...item,
        textContent: cleanText,
      });
      
      return {
        ...fallback,
        embedding: null,
        meta: {
          ...item.meta,
          cleanedText,
          ...fallback.meta,
        },
      };
    }
    
    // Step 2: AI enrichment (themes, topics, entities, analysis)
    const enrichment = await extractEnrichment(cleanText, {
      url: item.url,
      sourceType: item.sourceType,
      channel: item.channel,
    });
    
    // Step 3: Generate fallback for any missing fields
    // This ensures NO item ever has incomplete enrichment
    const fallback = fallbackClassifier.applyFallbackClassification({
      ...item,
      textContent: cleanText,
      extractedThemes: enrichment.extractedThemes,
      microTopics: enrichment.microTopics,
      entities: enrichment.entities,
      aiAnalysis: enrichment.aiAnalysis,
    });
    
    // Merge AI enrichment with fallback (AI takes priority)
    const merged = fallbackClassifier.mergeFallbackWithEnrichment(enrichment, fallback);
    
    // Step 4: Generate embedding
    let embedding = item.embedding || null;
    if (!skipEmbedding && !embedding) {
      // Build embedding text from enriched data
      const embeddingText = [
        item.title,
        item.description,
        cleanText.substring(0, 2000),
        ...merged.microTopics,
        ...merged.extractedThemes,
        merged.aiAnalysis.summary,
      ].filter(Boolean).join(' ');
      
      embedding = await generateEmbedding(embeddingText);
    }
    
    if (import.meta.env.DEV) console.log('[Enrichment] ✓ Complete:', {
      themes: merged.extractedThemes.length,
      topics: merged.microTopics.length,
      entities: merged.entities.length,
      hasEmbedding: !!embedding,
      usedFallback: merged.meta.fallbackUsed || false,
    });
    
    return {
      ...merged,
      embedding,
      meta: {
        ...item.meta,
        cleanedText,
        enrichedAt: new Date().toISOString(),
        ...(merged.meta || {}),
      },
    };
    
  } catch (error) {
    console.error('[Enrichment] Failed:', error);
    throw error;
  }
}

/**
 * Batch enrich multiple items
 */
export async function enrichItems(items, options = {}) {
  const results = [];
  const errors = [];
  
  for (const item of items) {
    try {
      const enriched = await enrichItem(item, options);
      results.push({ item, enriched });
    } catch (error) {
      errors.push({ item, error: error.message });
    }
  }
  
  return { results, errors };
}

/**
 * Check if item needs enrichment
 */
export function needsEnrichment(item) {
  const missingThemes = !item.extractedThemes || item.extractedThemes.length === 0;
  const missingTopics = !item.microTopics || item.microTopics.length === 0;
  const missingEmbedding = !item.embedding;
  const missingAnalysis = !item.aiAnalysis || !item.aiAnalysis.summary;
  
  return missingThemes || missingTopics || missingEmbedding || missingAnalysis;
}

/**
 * Validate enrichment quality
 */
export function validateEnrichment(enriched) {
  const errors = [];
  
  // Check themes
  if (!enriched.extractedThemes || enriched.extractedThemes.length === 0) {
    errors.push('Missing themes');
  } else {
    // Check for invalid themes
    const invalid = enriched.extractedThemes.filter(t => !ALLOWED_THEMES.includes(t.toLowerCase()));
    if (invalid.length > 0) {
      errors.push(`Invalid themes: ${invalid.join(', ')}`);
    }
  }
  
  // Check microtopics
  if (!enriched.microTopics || enriched.microTopics.length === 0) {
    errors.push('Missing microtopics');
  } else {
    // Check for generic microtopics
    const generic = enriched.microTopics.filter(t => {
      const lower = t.toLowerCase();
      return lower.includes('general') || 
             lower.includes('tips') || 
             lower.split(' ').length === 1;
    });
    if (generic.length > 0) {
      errors.push(`Generic microtopics detected: ${generic.join(', ')}`);
    }
  }
  
  // Check embedding
  if (!enriched.embedding) {
    errors.push('Missing embedding');
  }
  
  // Check AI analysis
  if (!enriched.aiAnalysis) {
    errors.push('Missing AI analysis');
  } else {
    if (!enriched.aiAnalysis.summary || enriched.aiAnalysis.summary.length < 20) {
      errors.push('AI summary too short or missing');
    }
    if (!enriched.aiAnalysis.whyItMatters || enriched.aiAnalysis.whyItMatters.length < 20) {
      errors.push('whyItMatters too short or missing');
    }
    if (!enriched.aiAnalysis.relatedConcepts || enriched.aiAnalysis.relatedConcepts.length === 0) {
      errors.push('Missing related concepts');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings: [],
  };
}

/**
 * Debug: Show enrichment for a single item
 */
export async function debugEnrich(item) {
  if (!import.meta.env.DEV) return;
  
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🔬 ENRICHMENT DEBUG');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('');
  console.log('INPUT ITEM:');
  console.log('  Title:', item.title);
  console.log('  URL:', item.url);
  console.log('  Source:', item.sourceType || item.source_type);
  console.log('');
  
  const cleanText = extractCleanText(item);
  console.log('EXTRACTED TEXT:');
  console.log(cleanText.substring(0, 500) + (cleanText.length > 500 ? '...' : ''));
  console.log('');
  
  console.log('Running AI enrichment...');
  const enriched = await enrichItem(item);
  
  console.log('');
  console.log('ENRICHMENT RESULTS:');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('');
  console.log('📊 THEMES:', enriched.extractedThemes);
  console.log('');
  console.log('🔍 MICROTOPICS:', enriched.microTopics);
  console.log('');
  console.log('🏷️ ENTITIES:', enriched.entities);
  console.log('');
  console.log('🧠 AI ANALYSIS:');
  console.log('  Summary:', enriched.aiAnalysis.summary);
  console.log('  Why it matters:', enriched.aiAnalysis.whyItMatters);
  console.log('  Related:', enriched.aiAnalysis.relatedConcepts);
  console.log('');
  console.log('📐 EMBEDDING:', enriched.embedding ? `✓ (${enriched.embedding.length} dims)` : '✗ None');
  console.log('');
  
  const validation = validateEnrichment(enriched);
  console.log('VALIDATION:', validation.valid ? '✅ PASS' : '❌ FAIL');
  if (!validation.valid) {
    console.log('Errors:', validation.errors);
  }
  console.log('');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  return enriched;
}

// Expose globally for debugging
if (typeof window !== 'undefined') {
  window.enrichmentService = {
    enrichItem,
    enrichItems,
    needsEnrichment,
    validateEnrichment,
    debugEnrich,
  };
  
  window.enrich = debugEnrich;
  
  if (import.meta.env.DEV) {
    console.log('[Enrichment] Debug commands available:');
    console.log('  window.enrich(item) - Test enrichment on single item');
  }
}

export default {
  enrichItem,
  enrichItems,
  needsEnrichment,
  validateEnrichment,
  debugEnrich,
  ALLOWED_THEMES,
};
